// §0.5.4 ACNM-Based Lab Flagging Thresholds — admin-configurable, stored in Firestore

export interface LabThresholdChangeLog {
  changedBy: string // admin userId
  changedAt: string // ISO timestamp
  previousValue: unknown
}

export interface LabFlaggingThreshold {
  id: string // unique key matching lab type or sub-value
  labName: string
  description: string
  // For numeric thresholds
  minValue?: number
  maxValue?: number
  unit?: string
  // For categorical thresholds (e.g. GBS positive, HIV positive)
  flaggedValues?: string[]
  // Configurable notes visible to provider when flagged
  clinicalNote?: string
  // Two-tier for platelets (<100K flag, <50K urgent)
  urgentMaxValue?: number
  // changeLog — last 20 entries
  changeLog?: LabThresholdChangeLog[]
}

export interface LabFlaggingThresholds {
  thresholds: Record<string, LabFlaggingThreshold>
}

// Default ACNM thresholds — used as fallback if Firestore document is absent
export const DEFAULT_LAB_FLAGGING_THRESHOLDS: Record<string, Omit<LabFlaggingThreshold, 'id'>> = {
  hemoglobin: {
    labName: 'Hemoglobin',
    description: 'ACNM pregnancy-specific threshold (hemodilution)',
    minValue: 10.0,
    unit: 'g/dL',
    clinicalNote: 'Pregnancy threshold <10 g/dL (not the standard 12 g/dL — hemodilution expected)',
  },
  hematocrit: {
    labName: 'Hematocrit',
    description: 'ACNM pregnancy-specific threshold',
    minValue: 30,
    unit: '%',
    clinicalNote: 'Flag when <30%',
  },
  platelets: {
    labName: 'Platelets',
    description: 'ACNM threshold — two-tier alert',
    minValue: 100,
    urgentMaxValue: 50,
    unit: '×10³/µL',
    clinicalNote: '<100K = flag; <50K = urgent additional alert',
  },
  bp_systolic: {
    labName: 'Blood Pressure (Systolic)',
    description: 'ACNM/ACOG hypertension threshold',
    maxValue: 140,
    unit: 'mmHg',
    clinicalNote: 'Triggers red text in chart and repeat-in-15-min prompt',
  },
  bp_diastolic: {
    labName: 'Blood Pressure (Diastolic)',
    description: 'ACNM/ACOG hypertension threshold',
    maxValue: 90,
    unit: 'mmHg',
    clinicalNote: '≥90 diastolic = flag',
  },
  gct: {
    labName: 'GCT (1-hour glucose challenge)',
    description: 'Configurable — some practices use 140',
    maxValue: 130,
    unit: 'mg/dL',
    clinicalNote: 'Default threshold 130 mg/dL. Configurable per practice.',
  },
  gtt_fasting: {
    labName: 'GTT Fasting (Carpenter-Coustan)',
    description: 'Two or more CC values met = GDM diagnosis',
    maxValue: 95,
    unit: 'mg/dL',
  },
  gtt_1h: {
    labName: 'GTT 1-Hour (Carpenter-Coustan)',
    description: 'Two or more CC values met = GDM diagnosis',
    maxValue: 180,
    unit: 'mg/dL',
  },
  gtt_2h: {
    labName: 'GTT 2-Hour (Carpenter-Coustan)',
    description: 'Two or more CC values met = GDM diagnosis',
    maxValue: 155,
    unit: 'mg/dL',
  },
  gtt_3h: {
    labName: 'GTT 3-Hour (Carpenter-Coustan)',
    description: 'Two or more CC values met = GDM diagnosis',
    maxValue: 140,
    unit: 'mg/dL',
  },
  urine_protein: {
    labName: 'Urine Protein (Dipstick)',
    description: 'ACNM — combined with elevated BP → preeclampsia alert',
    flaggedValues: ['2+', '3+', '4+'],
    clinicalNote:
      '≥2+ requires clinical review. Combined with BP elevation → preeclampsia protocol.',
  },
  gbs: {
    labName: 'GBS Culture',
    description: 'Triggers intrapartum antibiotic protocol reminder at delivery',
    flaggedValues: ['positive'],
    clinicalNote: 'GBS positive → intrapartum antibiotic protocol.',
  },
  rpr_syphilis: {
    labName: 'RPR / Syphilis',
    description: 'Triggers confirmatory testing prompt',
    flaggedValues: ['reactive'],
    clinicalNote: 'Reactive result → confirmatory testing required.',
  },
  hiv: {
    labName: 'HIV',
    description: 'Positive or declined — both flagged',
    flaggedValues: ['positive', 'declined'],
    clinicalNote: 'Positive = clinical management. Declined = counseling documentation required.',
  },
  rubella: {
    labName: 'Rubella',
    description: 'Non-immune → postpartum vaccine reminder',
    flaggedValues: ['non-immune'],
    clinicalNote: 'Non-immune → postpartum MMR vaccine.',
  },
  hep_b_sag: {
    labName: 'Hepatitis B sAg',
    description: 'Positive → neonatal prophylaxis reminder at delivery',
    flaggedValues: ['reactive', 'positive'],
    clinicalNote: 'HBsAg positive → neonatal HBIG + HBV vaccine at birth.',
  },
  rh_factor: {
    labName: 'Rh Factor',
    description: 'Negative → Rh-negative workflow: Rhogam at 28w, antibody screen',
    flaggedValues: ['negative'],
    clinicalNote: 'Rh-negative → Rhogam at 28w GA + antibody screen. See §2.7.1.',
  },
}

export const LAB_FLAGGING_THRESHOLDS_DOC = 'labFlaggingThresholds'
